import { useState, useEffect, useRef } from "react";
import config from "../config";

const API = config.API_BASE_URL;
const BOT_NAME = "ORCA AI Assistant";

// ── ORCA Logo (preserved) ─────────────────────────────────────────────────────
const OrcaAvatar = ({ size = 28 }) => (
  <div style={{
    width: size, height: size, borderRadius: size * 0.28,
    background: "#1d4ed8",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  }}>
    <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none"
      stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
      <circle cx="8.5" cy="10" r="1.2" fill="white" stroke="none" />
      <circle cx="12" cy="10" r="1.2" fill="white" stroke="none" />
      <circle cx="15.5" cy="10" r="1.2" fill="white" stroke="none" />
    </svg>
  </div>
);

// ── Greeting bubbles ──────────────────────────────────────────────────────────
const GREETING_BUBBLES = [
  { id: 1, text: "Hi there! Welcome to ORCA Helpdesk!", delay: 0 },
  { id: 2, text: "I'm your AI support assistant — here to help 24/7.", delay: 600 },
  { id: 3, text: "What would you like to do today?", delay: 1200 },
];

// ── Quick reply options ───────────────────────────────────────────────────────
const QUICK_REPLIES = [
  {
    id: "ticket", label: "Submit a Ticket",
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>,
    msg: "I want to submit a support ticket.",
  },
  {
    id: "status", label: "Check Ticket Status",
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
    msg: "I want to check my ticket status.",
  },
  {
    id: "faq", label: "Common Questions (FAQ)",
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
    msg: "Show me the common questions.",
  },
  {
    id: "human", label: "Talk to a Live Agent",
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
    msg: "I'd like to speak to a live agent.",
  },
];

// ── Follow-up questions per intent ────────────────────────────────────────────
const FOLLOW_UPS = {
  ticket: [
    {
      id: "ticket_new", label: "Create new ticket",
      icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
      msg: "I want to create a new ticket.",
    },
    {
      id: "ticket_urgent", label: "Mark as urgent",
      icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
      msg: "My issue is urgent.",
    },
    {
      id: "ticket_attach", label: "Attach a file",
      icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" /></svg>,
      msg: "I want to attach a file to my ticket.",
    },
  ],
  status: [
    {
      id: "status_mine", label: "View my tickets",
      icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
      msg: "Show my open tickets.",
    },
    {
      id: "status_track", label: "Track by ticket #",
      icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
      msg: "Track a specific ticket number.",
    },
    {
      id: "status_closed", label: "View resolved tickets",
      icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
      msg: "Show my resolved tickets.",
    },
  ],
  faq: [
    {
      id: "faq_reset", label: "Reset my password",
      icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>,
      msg: "How do I reset my password?",
    },
    {
      id: "faq_time", label: "Resolution time",
      icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
      msg: "How long does a ticket take to resolve?",
    },
    {
      id: "faq_escalate", label: "Escalate my issue",
      icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 11 21 7 17 3" /><line x1="21" y1="7" x2="9" y2="7" /><polyline points="7 21 3 17 7 13" /><line x1="15" y1="17" x2="3" y2="17" /></svg>,
      msg: "How do I escalate my issue?",
    },
  ],
  human: [],
};

// ── Bot scripted responses ────────────────────────────────────────────────────
const BOT_RESPONSES = {
  ticket: [
    "Sure! To submit a ticket, please describe your issue below and I'll take care of it right away.",
    "Our support team will be assigned to your case within minutes. What would you like to do?",
  ],
  ticket_new: [
    "Please type a brief description of your issue and I'll create the ticket for you now.",
  ],
  ticket_urgent: [
    "Got it — I'll mark this as URGENT. Please type your concern so I can escalate it immediately.",
  ],
  ticket_attach: [
    "You can attach files when submitting your ticket from the dashboard. Head to My Requests and click Attach File.",
    "Supported formats: PDF, Excel, images (JPG/PNG). Max 10MB per file.",
  ],
  status: [
    "To check your ticket status, go to Dashboard → My Tickets.",
    "You can see real-time updates, admin replies, and resolution status. What specifically do you need?",
  ],
  status_mine: [
    "Your open tickets are visible under Dashboard → My Tickets → Open.",
    "Each ticket shows the current status, assigned agent, and latest reply.",
  ],
  status_track: [
    "Please type your ticket number (e.g. TICKET-20260227-111241-938) and I'll pull up the details.",
  ],
  status_mine: [
    "Please type your ticket number and I'll show your open tickets.",
  ],
  status_closed: [
    "Please type your ticket number and I'll check the resolved status for you.",
  ],

  faq: [
    "Here are our most common questions — pick one below or type your question!",
  ],
  faq_reset: [
    "To reset your password: go to the login page and click 'Forgot Password'.",
    "A reset link will be sent to your registered email. Link expires in 30 minutes.",
  ],
  faq_time: [
    "Standard tickets are resolved within 24–48 hours.",
    "Urgent tickets are prioritized and usually resolved within 4–8 hours.",
  ],
  faq_escalate: [
    "To escalate: open your ticket and click the 'Escalate' button, or reply 'ESCALATE' in the ticket thread.",
    "A senior agent will be assigned to your case within 1 hour.",
  ],
  human: [
    "Connecting you to a live support agent now.",
    "Please wait while I notify the team. You can also type your concern below and an agent will reply shortly!",
  ],
  default: [
    "Got it! Let me look into that for you.",
    "A support agent will reach out shortly. Feel free to add more details!",
  ],
};

// ── Typing dots ───────────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 7, marginTop: 4 }}>
      <OrcaAvatar size={26} />
      <div style={{ background: "#f1f5f9", border: "0.5px solid #e2e8f0", borderRadius: "4px 14px 14px 14px", padding: "10px 14px", display: "flex", gap: 5, alignItems: "center" }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#93c5fd", display: "inline-block", animation: `botDot 1.2s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
      </div>
    </div>
  );
}

// ── Bot bubble ────────────────────────────────────────────────────────────────
function BotBubble({ text, visible }) {
  if (!visible) return null;
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 7, marginTop: 4, animation: "botUp 0.28s cubic-bezier(.34,1.56,.64,1)" }}>
      <OrcaAvatar size={26} />
      <div style={{ background: "#f8fafc", border: "0.5px solid #e2e8f0", color: "#1e293b", borderRadius: "4px 14px 14px 14px", padding: "9px 13px", fontSize: 13, lineHeight: 1.55, maxWidth: "78%", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {text}
      </div>
    </div>
  );
}

// ── User bubble ───────────────────────────────────────────────────────────────
function UserBubble({ text }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4, animation: "botUp 0.22s ease" }}>
      <div style={{ background: "#1d4ed8", color: "#fff", borderRadius: "14px 4px 14px 14px", padding: "9px 13px", fontSize: 13, lineHeight: 1.55, maxWidth: "78%", wordBreak: "break-word" }}>
        {text}
      </div>
    </div>
  );
}

// ── Quick reply button ────────────────────────────────────────────────────────
function QRButton({ item, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={() => onClick(item)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "center", gap: 7,
        background: hov ? "#eff6ff" : "#fff",
        color: "#1d4ed8",
        border: `0.5px solid ${hov ? "#3b82f6" : "#bfdbfe"}`,
        borderRadius: 20, padding: "7px 14px",
        fontSize: 12.5, fontWeight: 500, cursor: "pointer",
        textAlign: "left", transition: "all 0.15s",
        fontFamily: "system-ui,sans-serif",
      }}
    >
      {item.icon}
      {item.label}
    </button>
  );
}

// ── Main AIChatBot ────────────────────────────────────────────────────────────
export default function AIChatBot({ userData, onHandoff, autoOpen = false }) {
  const [open, setOpen] = useState(autoOpen);
  const [phase, setPhase] = useState("greeting");
  const [visibleGreets, setVisible] = useState([]);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [quickReplies, setQuick] = useState([]);
  const [inputVal, setInputVal] = useState("");
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const msgId = useRef(0);
  const nextId = () => ++msgId.current;

  // ── Greeting bubbles on open ──────────────────────────────────────────────
  useEffect(() => {
    if (!open || phase !== "greeting") return;
    GREETING_BUBBLES.forEach(({ id, delay }) => {
      setTimeout(() => setVisible(prev => [...prev, id]), delay + 400);
    });
  }, [open]);

  // ── Auto scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing, visibleGreets, quickReplies]);

  // ── Post message to backend ───────────────────────────────────────────────
  const postToBackend = async (content) => {
    if (!userData?.username) return;
    try {
      await fetch(`${API}/Messages/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: userData.username, admin: "admin", content }),
      });
    } catch (err) { console.warn("Bot backend post failed:", err); }
  };

  // ── Add bot message with delay ────────────────────────────────────────────
  const addBotMsg = (text, delay = 0, showTyping = true) => {
    if (showTyping && delay > 0) setTimeout(() => setTyping(true), Math.max(0, delay - 700));
    setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, { id: nextId(), from: "bot", text }]);
    }, delay);
  };

  // ── Get Started ───────────────────────────────────────────────────────────
  const handleGetStarted = () => {
    setPhase("started");
    setUnread(0);
    const firstName = userData?.fullNames?.split(" ")[0] ?? "there";
    addBotMsg(`Nice to meet you, ${firstName}! How can I help you today?`, 0, false);
    setTimeout(() => setQuick(QUICK_REPLIES), 500);
  };

  // ── Main quick reply ──────────────────────────────────────────────────────
  const handleQuickReply = (item) => {
    setQuick([]);
    setMessages(prev => [...prev, { id: nextId(), from: "user", text: item.label }]);
    postToBackend(`${item.msg}`);

    const responses = BOT_RESPONSES[item.id] ?? BOT_RESPONSES.default;
    responses.forEach((text, i) => addBotMsg(text, 800 + i * 1000, i === 0));

    const afterDelay = 800 + responses.length * 1000 + 300;

    if (item.id === "human") {
      // Direct handoff
      setTimeout(() => {
        setTyping(false);
        setMessages(prev => [...prev, { id: nextId(), from: "", text: "Ready to connect you to a live agent. Click below to proceed.", isHandoff: true }]);
        setPhase("handoff");
      }, afterDelay);
    } else {
      // Show follow-up options
      const followUps = FOLLOW_UPS[item.id] ?? [];
      setTimeout(() => {
        setTyping(false);
        if (followUps.length > 0) {
          setQuick(followUps);
          setMessages(prev => [...prev, { id: nextId(), from: "", text: "What would you like to do next?", isFollowup: true }]);
        } else {
          setMessages(prev => [...prev, { id: nextId(), from: "", text: "Is there anything else I can help you with?", isHandoff: true }]);
        }
        setPhase("replied");
      }, afterDelay);
    }
  };

  // ── Follow-up quick reply ─────────────────────────────────────────────────
  const handleFollowUp = (item) => {
    setQuick([]);
    setMessages(prev => [...prev, { id: nextId(), from: "user", text: item.label }]);
    postToBackend(`[Bot Follow-up] ${item.msg}`);

    const responses = BOT_RESPONSES[item.id] ?? BOT_RESPONSES.default;
    responses.forEach((text, i) => addBotMsg(text, 800 + i * 1000, i === 0));

    const afterDelay = 800 + responses.length * 1000 + 300;
    if (item.id === "status_track" || item.id === "status_mine" || item.id === "status_closed") {
      setPhase("awaiting_ticket");
      return;
    }
    setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, {
        id: nextId(), from: "",
        text: "Would you like to connect to a live agent for further assistance?",
        isHandoff: true,
      }]);
      setPhase("handoff");
    }, afterDelay);
  };

  const fetchTicketStatus = async (ticketNum) => {
    setTyping(true);
    try {
      const res = await fetch(`${API}/Tickets/${ticketNum}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTyping(false);
      setMessages(prev => [...prev,
      {
        id: nextId(), from: "", text:
          `Ticket: ${data.ticketNumber}\nSubject: ${data.subject}\nName: ${data.name}\nStatus: ${data.status}\nPriority: ${data.priority}\nUrgency: ${data.urgency}`
      },
      { id: nextId(), from: "", text: "Would you like to connect to a live agent?", isHandoff: true }
      ]);
      setPhase("handoff");
    } catch {
      setTyping(false);
      addBotMsg("Hindi ko mahanap ang ticket na yan. Check mo ulit ang number.", 0, false);
      setPhase("awaiting_ticket");
    }
  };
  // ── Free text send ────────────────────────────────────────────────────────
  const handleSend = () => {
    const text = inputVal.trim();
    if (!text) return;
    setInputVal("");
    setQuick([]);
    setMessages(prev => [...prev, { id: nextId(), from: "user", text }]);
    postToBackend(text);

    if (phase === "awaiting_ticket") {
      setPhase("replied");
      fetchTicketStatus(text);
      return;
    }
    const lower = text.toLowerCase();
    let intent = "default";
    if (lower.includes("ticket") || lower.includes("issue") || lower.includes("problem") || lower.includes("bug")) intent = "ticket";
    else if (lower.includes("status") || lower.includes("check") || lower.includes("track") || lower.includes("update")) intent = "status";
    else if (lower.includes("faq") || lower.includes("question") || lower.includes("how") || lower.includes("help")) intent = "faq";
    else if (lower.includes("human") || lower.includes("agent") || lower.includes("live") || lower.includes("person") || lower.includes("support")) intent = "human";
    else if (lower.includes("password") || lower.includes("reset")) intent = "faq_reset";
    else if (lower.includes("escalat")) intent = "faq_escalate";

    const responses = BOT_RESPONSES[intent] ?? BOT_RESPONSES.default;
    responses.forEach((t, i) => addBotMsg(t, 800 + i * 1000, i === 0));

    const afterDelay = 800 + responses.length * 1000 + 300;

    if (intent === "human") {
      setTimeout(() => {
        setTyping(false);
        setMessages(prev => [...prev, { id: nextId(), from: "", text: "Ready to connect you to a live agent. Click below to proceed.", isHandoff: true }]);
        setPhase("handoff");
      }, afterDelay);
    } else {
      const followUps = FOLLOW_UPS[intent] ?? [];
      setTimeout(() => {
        setTyping(false);
        if (followUps.length > 0) {
          setMessages(prev => [...prev, { id: nextId(), from: "", text: "What would you like to do next?", isFollowup: true }]);
          setQuick(followUps);
        } else {
          setMessages(prev => [...prev, { id: nextId(), from: "", text: "Would you like to connect to a live support agent?", isHandoff: true }]);
          setPhase("handoff");
        }
        setPhase("replied");
      }, afterDelay);
    }
  };

  // ── Handoff to live agent ─────────────────────────────────────────────────
  const handleHandoff = () => {
    setMessages(prev => [...prev, { id: nextId(), from: "", text: "Connecting you to a live agent now. Please hold on..." }]);
    setTimeout(() => {
      setOpen(false);
      if (onHandoff) onHandoff();
    }, 1400);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ── Decide which set of quick replies to dispatch ─────────────────────────
  const dispatchQuickReply = (item) => {
    const isMainIntent = QUICK_REPLIES.some(q => q.id === item.id);
    if (isMainIntent) handleQuickReply(item);
    else handleFollowUp(item);
  };

  return (
    <>
      <style>{`
        @keyframes botDot { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }
        @keyframes botUp  { from{transform:translateY(8px);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes botPop { from{transform:scale(0.72);opacity:0} to{transform:scale(1);opacity:1} }
        .bot-scroll::-webkit-scrollbar{width:3px}
        .bot-scroll::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:4px}
        .bot-input::placeholder{color:#94a3b8}
        .bot-fab:hover{transform:scale(1.08)!important}
        .bot-send:hover{opacity:0.85}
      `}</style>

      <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 10003, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12 }}>

        {/* ── Chat window ── */}
        {open && (
          <div style={{ width: 345, background: "#fff", borderRadius: 20, border: "0.5px solid #e2e8f0", boxShadow: "0 12px 40px rgba(0,0,0,0.14)", display: "flex", flexDirection: "column", overflow: "hidden", animation: "botPop 0.28s cubic-bezier(.34,1.56,.64,1)" }}>

            {/* ── Header ── */}
            <div style={{ background: "#1d4ed8", padding: "11px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ position: "relative" }}>
                  <OrcaAvatar size={38} />
                  <span style={{ position: "absolute", bottom: 0, right: 0, width: 9, height: 9, borderRadius: "50%", background: "#4ade80", border: "2px solid #1d4ed8" }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13.5, color: "#fff", letterSpacing: 0.01 }}>{BOT_NAME}</div>
                  <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.75)", display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#4ade80", display: "inline-block" }} />
                    Online · always here
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 8, width: 28, height: 28, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>

            {/* ── Messages ── */}
            <div className="bot-scroll" style={{ flex: 1, overflowY: "auto", padding: "14px 12px 10px", display: "flex", flexDirection: "column", minHeight: 280, maxHeight: 360, background: "#f8fafc" }}>

              {GREETING_BUBBLES.map(({ id, text }) => (
                <BotBubble key={id} text={text} visible={visibleGreets.includes(id)} />
              ))}

              {phase === "greeting" && visibleGreets.length === GREETING_BUBBLES.length && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 16, animation: "botPop 0.3s ease" }}>
                  <button
                    onClick={handleGetStarted}
                    style={{ background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 24, padding: "10px 26px", fontSize: 13.5, fontWeight: 700, cursor: "pointer", letterSpacing: 0.2, display: "flex", alignItems: "center", gap: 7, fontFamily: "system-ui,sans-serif" }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                    Get Started
                  </button>
                </div>
              )}

              {messages.map(m => (
                <div key={m.id}>
                  {m.from === ""
                    ? <BotBubble text={m.text} visible={true} />
                    : <UserBubble text={m.text} />
                  }

                  {/* Handoff buttons */}
                  {m.isHandoff && (phase === "replied" || phase === "handoff") && (
                    <div style={{ display: "flex", gap: 7, marginTop: 8, marginLeft: 33, flexWrap: "wrap", animation: "botPop 0.28s ease" }}>
                      <button
                        onClick={handleHandoff}
                        style={{ background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 20, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "system-ui,sans-serif" }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                        Connect to Live Agent
                      </button>
                      <button
                        onClick={() => { setPhase("done"); setQuick(QUICK_REPLIES); addBotMsg("No problem! Is there anything else I can help you with?", 0, false); }}
                        style={{ background: "#f1f5f9", color: "#475569", border: "0.5px solid #e2e8f0", borderRadius: 20, padding: "7px 14px", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "system-ui,sans-serif" }}
                      >
                        No thanks
                      </button>
                    </div>
                  )}

                  {/* Follow-up indicator — quick replies rendered below */}
                </div>
              ))}

              {typing && <TypingIndicator />}

              {/* Quick replies / follow-ups */}
              {quickReplies.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10, marginLeft: 33, animation: "botUp 0.28s ease" }}>
                  {quickReplies.map(qr => (
                    <QRButton key={qr.id} item={qr} onClick={dispatchQuickReply} />
                  ))}
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            {/* ── Input bar ── */}
            {phase !== "greeting" && (
              <div style={{ padding: "9px 12px", borderTop: "0.5px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <div style={{ flex: 1, background: "#f1f5f9", border: "0.5px solid #e2e8f0", borderRadius: 22, padding: "8px 13px", display: "flex", alignItems: "center", gap: 7 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                  <input
                    className="bot-input"
                    value={inputVal}
                    onChange={e => setInputVal(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="Type a message..."
                    style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 13, color: "#1e293b", fontFamily: "system-ui,sans-serif" }}
                  />
                </div>
                <button
                  className="bot-send"
                  onClick={handleSend}
                  disabled={!inputVal.trim()}
                  style={{ background: inputVal.trim() ? "#1d4ed8" : "#e2e8f0", border: "none", borderRadius: "50%", width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: inputVal.trim() ? "pointer" : "default", flexShrink: 0, transition: "all 0.2s" }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill={inputVal.trim() ? "#fff" : "#94a3b8"}><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
                </button>
              </div>
            )}

            {/* ── Footer ── */}
            <div style={{ padding: "5px 12px 8px", background: "#fff", borderTop: "0.5px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
              <span style={{ fontSize: 10, color: "#94a3b8", fontFamily: "system-ui,sans-serif" }}>Secured by ORCA Helpdesk</span>
            </div>

          </div>
        )}

        {/* ── Floating button ── */}
        <button
          className="bot-fab"
          onClick={() => { setOpen(v => !v); setUnread(0); }}
          style={{ width: 52, height: 52, borderRadius: 16, background: "#1d4ed8", border: "none", cursor: "pointer", boxShadow: "0 4px 18px rgba(29,78,216,0.38)", display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.2s", position: "relative" }}
        >
          {open
            ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <path d="M8 21h8M12 17v4" />
              <circle cx="8.5" cy="10" r="1.2" fill="white" stroke="none" />
              <circle cx="12" cy="10" r="1.2" fill="white" stroke="none" />
              <circle cx="15.5" cy="10" r="1.2" fill="white" stroke="none" />
            </svg>
          }
          {!open && unread > 0 && (
            <div style={{ position: "absolute", top: -5, right: -5, background: "#ef4444", color: "#fff", borderRadius: "50%", width: 19, height: 19, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff" }}>
              {unread > 9 ? "9+" : unread}
            </div>
          )}
        </button>

      </div>
    </>
  );
}